import React, { useState, useEffect } from 'react';
import { X, Save, Plus, Trash2, HelpCircle } from 'lucide-react';
import { preparePlacementForEdit } from '../utils/dataNormalizers';
import { MEDIA_TYPES, VIDEO_CONTEXTS, VIDEO_MIMES, VIDEO_PROTOCOLS, VIDEO_PLAYBACK_METHODS } from '../utils/constants';
import { validatePlacementSafe } from '../schemas/index.js';
import { migratePlacementSafe } from '../utils/configMigration.js';
import { AVAILABLE_BIDDERS } from '../config/prebidOptions';

const PlacementEditModal = ({ placement, placementName, isOpen, onClose, onSave, config }) => {
  const [editedPlacement, setEditedPlacement] = useState(null);

  // Helper function to render bidder parameter fields dynamically
  const renderBidderParameterFields = (bidderName, params, contextId) => {
    const bidderConfig = AVAILABLE_BIDDERS[bidderName.toLowerCase()];
    
    if (!bidderConfig) {
      // Unknown bidder - show JSON editor
      // Ensure params is a valid object for JSON stringification
      const safeParams = params && typeof params === 'object' ? params : {};
      
      return (
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Parameters (JSON)
          </label>
          <textarea
            value={JSON.stringify(safeParams, null, 2)}
            onChange={(e) => {
              try {
                // Only parse if value is not empty and looks like JSON
                const trimmedValue = e.target.value.trim();
                if (trimmedValue && (trimmedValue.startsWith('{') || trimmedValue.startsWith('['))) {
                  const parsed = JSON.parse(trimmedValue);
                  updateBidderParam(contextId, bidderName, null, parsed);
                } else {
                  // Invalid JSON format, don't update
                  console.warn('Invalid JSON format for bidder params:', trimmedValue);
                }
              } catch (err) {
                // Invalid JSON, don't update - this is expected during typing
                console.debug('JSON parse error (expected during editing):', err.message);
              }
            }}
            className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows="3"
            placeholder="{}"
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter valid JSON object for bidder parameters
          </p>
        </div>
      );
    }

    // Known bidder - render ONLY placement-specific form fields
    const placementParams = bidderConfig.placementParams || [];
    
    // If no placement-specific parameters, show info message
    if (placementParams.length === 0) {
      return (
        <div key={`${bidderName}-no-params`} className="text-sm text-gray-500 italic">
          This bidder has no placement-specific parameters.
        </div>
      );
    }
    
    return placementParams.map(paramName => {
      const paramType = bidderConfig.paramTypes[paramName];
      const paramDescription = bidderConfig.paramDescriptions[paramName];
      const isRequired = bidderConfig.requiredParams.includes(paramName);
      const currentValue = params[paramName] || '';
      
      return (
        <div key={paramName} className={placementParams.length <= 2 ? '' : 'col-span-1'}>
          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
            {paramDescription || paramName}
            {isRequired && <span className="text-red-500">*</span>}
            {paramDescription && (
              <HelpCircle 
                size={14} 
                className="text-gray-400 hover:text-gray-600 cursor-help" 
                title={paramDescription}
              />
            )}
          </label>
          {paramType === 'boolean' ? (
            <select
              value={currentValue.toString()}
              onChange={(e) => updateBidderParam(contextId, bidderName, paramName, e.target.value === 'true')}
              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="true">True</option>
              <option value="false">False</option>
            </select>
          ) : (
            <input
              type={paramType === 'number' ? 'number' : 'text'}
              value={currentValue}
              onChange={(e) => {
                const value = paramType === 'number' ? 
                  (e.target.value === '' ? '' : parseFloat(e.target.value)) : 
                  e.target.value;
                updateBidderParam(contextId, bidderName, paramName, value);
              }}
              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={paramType === 'number' ? '0' : `Enter ${paramDescription || paramName}`}
            />
          )}
        </div>
      );
    });
  };

  useEffect(() => {
    if (placement) {
      // First migrate to new structure, then normalize
      const migrationResult = migratePlacementSafe(placement);
      let processedPlacement;
      
      if (migrationResult.success) {
        processedPlacement = migrationResult.data;
        if (migrationResult.migrated) {
          console.log(`✅ Migrated placement ${placementName} to new schema`);
        }
      } else {
        console.warn(`❌ Migration failed for ${placementName}:`, migrationResult.errors);
        processedPlacement = placement;
      }
      
      // Then apply existing normalization
      const normalizedPlacement = preparePlacementForEdit(processedPlacement, placementName || 'new-placement');
      setEditedPlacement(normalizedPlacement);
    }
  }, [placement, placementName]);

  const handleSave = () => {
    // Validate placement with Zod schema before saving
    const validationResult = validatePlacementSafe(editedPlacement);
    
    if (validationResult.success) {
      onSave(placementName, validationResult.data);
      onClose();
    } else {
      console.error('❌ Placement validation failed:', validationResult.errors);
      alert(`Placement validation failed:\n${validationResult.errors.map(e => `• ${e.path}: ${e.message}`).join('\n')}`);
    }
  };

  const addBidder = (contextId) => {
    // Only show globally enabled bidders
    const enabledBidders = Object.entries(config?.bidders || {})
      .filter(([_, settings]) => settings.enabled)
      .map(([bidderName, _]) => bidderName);
    
    if (enabledBidders.length === 0) {
      alert('No bidders are globally enabled. Please enable bidders in the Prebid Config tab first.');
      return;
    }
    
    const availableBidders = enabledBidders.join(', ');
    const newBidder = prompt(`Enter bidder name (${availableBidders}):`);
    
    if (newBidder && newBidder.trim()) {
      const bidderKey = newBidder.trim().toLowerCase();
      
      // Validate that this is an enabled bidder
      if (!enabledBidders.includes(bidderKey)) {
        alert(`Bidder not globally enabled: ${newBidder}. Enabled bidders: ${availableBidders}`);
        return;
      }
      
      // Set default parameters based on bidder configuration
      let defaultParams = {};
      const bidderConfig = AVAILABLE_BIDDERS[bidderKey];
      
      if (bidderConfig && bidderConfig.requiredParams) {
        // Use configuration to set default parameters
        bidderConfig.requiredParams.forEach(param => {
          const paramType = bidderConfig.paramTypes[param];
          
          // Set smart defaults based on parameter type and site config
          if (param === 'networkId' && config?.bidders?.criteo?.networkId) {
            defaultParams[param] = config.bidders.criteo.networkId;
          } else if (param === 'siteId' && config?.bidders?.smartadserver?.siteId) {
            defaultParams[param] = config.bidders.smartadserver.siteId;
          } else if (param === 'pageId' && config?.bidders?.smartadserver?.pageId) {
            defaultParams[param] = config.bidders.smartadserver.pageId;
          } else if (param === 'inventoryCode') {
            // Generate smart inventory code for TripleLift
            const siteDomain = config?.site?.domain?.replace(/\./g, '_') || 'site';
            defaultParams[param] = `${siteDomain}_${placementName || 'placement'}_desktop_300x250`;
          } else {
            // Set appropriate empty default based on type
            defaultParams[param] = paramType === 'number' ? null : '';
          }
        });
      }
      
      setEditedPlacement(prev => {
        // Safe access with null checks and validation
        if (!prev || !prev.contexts) {
          console.error('Invalid placement structure in addBidder');
          return prev;
        }
        
        const context = prev.contexts[contextId] || {};
        const bidderParams = context.bidderParams || {};
        
        // Check if bidder already exists
        if (bidderParams[bidderKey]) {
          alert(`Bidder ${bidderKey} already exists in this context`);
          return prev;
        }
        
        console.log(`Adding bidder ${bidderKey} with params:`, defaultParams);
        
        return {
          ...prev,
          contexts: {
            ...prev.contexts,
            [contextId]: {
              ...context,
              bidderParams: {
                ...bidderParams,
                [bidderKey]: defaultParams
              }
            }
          }
        };
      });
    }
  };

  const removeBidder = (contextId, bidderName) => {
    setEditedPlacement(prev => {
      // Safe access with null checks
      const context = prev.contexts?.[contextId] || {};
      const bidderParams = context.bidderParams || {};
      const newBidderParams = { ...bidderParams };
      delete newBidderParams[bidderName];
      
      return {
        ...prev,
        contexts: {
          ...prev.contexts,
          [contextId]: {
            ...context,
            bidderParams: newBidderParams
          }
        }
      };
    });
  };

  const updateBidderParam = (contextId, bidderName, paramKey, paramValue) => {
    setEditedPlacement(prev => {
      // Safe access with null checks
      const context = prev.contexts?.[contextId] || {};
      const bidderParams = context.bidderParams || {};
      const existingBidderParams = bidderParams[bidderName] || {};
      
      return {
        ...prev,
        contexts: {
          ...prev.contexts,
          [contextId]: {
            ...context,
            bidderParams: {
              ...bidderParams,
              [bidderName]: paramKey === null ? paramValue : {
                ...existingBidderParams,
                [paramKey]: paramValue
              }
            }
          }
        }
      };
    });
  };

  const addSize = (contextId, device) => {
    const width = prompt('Enter width:');
    const height = prompt('Enter height:');
    if (width && height) {
      setEditedPlacement(prev => {
        // Safe access with null checks
        const context = prev.contexts?.[contextId] || {};
        const sizes = context.sizes || { desktop: [], mobile: [] };
        const deviceSizes = sizes[device] || [];
        
        return {
          ...prev,
          contexts: {
            ...prev.contexts,
            [contextId]: {
              ...context,
              sizes: {
                ...sizes,
                [device]: [...deviceSizes, [parseInt(width), parseInt(height)]]
              }
            }
          }
        };
      });
    }
  };

  const removeSize = (contextId, device, index) => {
    setEditedPlacement(prev => {
      // Safe access with null checks
      const context = prev.contexts?.[contextId] || {};
      const sizes = context.sizes || { desktop: [], mobile: [] };
      const deviceSizes = sizes[device] || [];
      
      return {
        ...prev,
        contexts: {
          ...prev.contexts,
          [contextId]: {
            ...context,
            sizes: {
              ...sizes,
              [device]: deviceSizes.filter((_, i) => i !== index)
            }
          }
        }
      };
    });
  };

  const addContext = () => {
    const contextDefinitions = config?.site?.contextDefinitions || {
      1: 'article',
      2: 'homepage', 
      3: 'category'
    };
    
    // Show available contexts
    const availableContexts = Object.entries(contextDefinitions)
      .filter(([contextId]) => !editedPlacement.contexts?.[contextId])
      .map(([contextId, contextName]) => `${contextId}: ${contextName}`)
      .join('\n');
    
    if (availableContexts) {
      const contextIdStr = prompt(`Select context ID:\n${availableContexts}\n\nEnter context ID:`);
      const contextId = parseInt(contextIdStr);
      
      if (contextId && contextDefinitions[contextId] && !editedPlacement.contexts?.[contextId]) {
        const contextName = contextDefinitions[contextId];
        setEditedPlacement(prev => ({
          ...prev,
          contexts: {
            ...prev.contexts,
            [contextId]: {
              enabled: false,
              sizes: {
                desktop: [[300, 250]],
                mobile: [[300, 250]]
              },
              gamPath: `/${config?.site?.gamNetworkId || '22264327084'}/${config?.site?.siteId || 'site'}_${contextName}/${placementName}`,
              bidderParams: {}
            }
          }
        }));
      }
    } else {
      alert('No more contexts available to add');
    }
  };

  const removeContext = (contextId) => {
    const contextDefinitions = config?.site?.contextDefinitions || {
      1: 'article',
      2: 'homepage', 
      3: 'category'
    };
    const contextName = contextDefinitions[contextId] || `Context ${contextId}`;
    
    if (window.confirm(`Are you sure you want to remove the ${contextName} context?`)) {
      setEditedPlacement(prev => {
        const { [contextId]: removed, ...remainingContexts } = prev.contexts;
        return {
          ...prev,
          contexts: remainingContexts
        };
      });
    }
  };

  if (!isOpen || !editedPlacement) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {placement ? `Edit ${placementName}` : `Add New Placement`}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Settings */}
          <div>
            <h3 className="text-lg font-medium mb-4">Basic Settings</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selector
                </label>
                <input
                  type="text"
                  value={editedPlacement.selector}
                  onChange={(e) => setEditedPlacement(prev => ({ ...prev, selector: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Media Type
                </label>
                <select
                  value={editedPlacement.mediaType || 'banner'}
                  onChange={(e) => setEditedPlacement(prev => ({
                    ...prev,
                    mediaType: e.target.value,
                    // Initialize video settings at placement level when switching to video/both
                    videoSettings: e.target.value === 'video' || e.target.value === 'both' 
                      ? prev.videoSettings || {
                          context: 'outstream',
                          playerSize: [640, 360],
                          mimes: ['video/mp4'],
                          protocols: [2, 3, 5, 6],
                          playbackmethod: [6],
                          minduration: 5,
                          maxduration: 30,
                          api: [2, 7], // VPAID 2.0 and OMID 1.0
                          skip: 1,
                          skipafter: 6,
                          renderer: {
                            url: 'https://cdn.jsdelivr.net/npm/in-renderer-js@1/dist/in-renderer.umd.min.js',
                            render: 'function(bid) { var inRenderer = new window.InRenderer(); inRenderer.render(bid.adUnitCode, bid); }'
                          },
                          rendererOptions: {
                            autoPlay: true,
                            mute: true,
                            adText: 'Advertisement'
                          }
                        }
                      : undefined
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="banner">Banner Only</option>
                  <option value="video">Video Only</option>
                  <option value="both">Banner + Video</option>
                </select>
              </div>
            </div>
          </div>

          {/* Contexts */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Contexts</h3>
              <button
                onClick={addContext}
                className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus size={16} className="mr-1" />
                Add Context
              </button>
            </div>
            {editedPlacement.contexts && Object.entries(editedPlacement.contexts).map(([contextId, context]) => {
              // Defensive checks for context data
              if (!context || typeof context !== 'object') {
                console.warn(`Invalid context data for contextId ${contextId}:`, context);
                return null;
              }
              
              const contextDefinitions = config?.site?.contextDefinitions || {
                1: 'article',
                2: 'homepage', 
                3: 'category'
              };
              const contextName = contextDefinitions[contextId] || `Context ${contextId}`;
              
              return (
                <div key={contextId} className="border border-gray-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium capitalize">{contextName} (ID: {contextId})</h4>
                  <div className="flex items-center space-x-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={context.enabled}
                        onChange={(e) => setEditedPlacement(prev => {
                          const context = prev.contexts?.[contextId] || {};
                          return {
                            ...prev,
                            contexts: {
                              ...prev.contexts,
                              [contextId]: {
                                ...context,
                                enabled: e.target.checked
                              }
                            }
                          };
                        })}
                        className="mr-2"
                      />
                      Enabled
                    </label>
                    {Object.keys(editedPlacement.contexts || {}).length > 1 && (
                      <button
                        onClick={() => removeContext(contextId)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>

                {/* GAM Path */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GAM Path
                  </label>
                  <input
                    type="text"
                    value={context.gamPath || ''}
                    onChange={(e) => setEditedPlacement(prev => {
                      const context = prev.contexts?.[contextId] || {};
                      return {
                        ...prev,
                        contexts: {
                          ...prev.contexts,
                          [contextId]: {
                            ...context,
                            gamPath: e.target.value
                          }
                        }
                      };
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Sizes */}
                <div className="mb-4">
                  <h5 className="font-medium mb-2">Ad Sizes</h5>
                  {['desktop', 'mobile'].map(device => (
                    <div key={device} className="mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium capitalize">{device}</span>
                        <button
                          onClick={() => addSize(contextId, device)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          + Add Size
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(context.sizes?.[device] || []).map((size, index) => (
                          <div key={index} className="flex items-center bg-gray-100 rounded px-2 py-1">
                            <span className="text-sm">{size[0]}×{size[1]}</span>
                            <button
                              onClick={() => removeSize(contextId, device, index)}
                              className="ml-2 text-red-600 hover:text-red-800"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bidders */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-medium">Bidders</h5>
                    <button
                      onClick={() => addBidder(contextId)}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      + Add Bidder
                    </button>
                  </div>
                  <div className="space-y-3">
                    {Object.entries(context.bidderParams || {}).map(([bidderName, params]) => (
                      <div key={bidderName} className="border border-gray-200 rounded p-3">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-medium capitalize text-lg">{bidderName}</span>
                          <button
                            onClick={() => removeBidder(contextId, bidderName)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        
                        {/* Bidder Parameters - Show Banner and Video separately when mediaType is 'both' */}
                        {(editedPlacement.mediaType === 'both') ? (
                          <div className="space-y-4">
                            {/* Banner Parameters */}
                            <div className="border border-blue-200 rounded-lg p-3 bg-blue-50">
                              <h6 className="font-medium text-blue-800 mb-3 flex items-center">
                                📺 Banner Parameters
                              </h6>
                              <div className="grid grid-cols-2 gap-3">
                                {bidderName.toLowerCase() === 'teads' && (
                                  <>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Placement ID
                                      </label>
                                      <input
                                        type="number"
                                        value={params.placementId || ''}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          updateBidderParam(contextId, bidderName, 'placementId', value === '' ? '' : parseInt(value));
                                        }}
                                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Page ID
                                      </label>
                                      <input
                                        type="number"
                                        value={params.pageId || ''}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          updateBidderParam(contextId, bidderName, 'pageId', value === '' ? '' : parseInt(value));
                                        }}
                                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      />
                                    </div>
                                  </>
                                )}
                                
                                {bidderName.toLowerCase() === 'triplelift' && (
                                  <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Inventory Code
                                    </label>
                                    <input
                                      type="text"
                                      value={params.inventoryCode || ''}
                                      onChange={(e) => updateBidderParam(contextId, bidderName, 'inventoryCode', e.target.value)}
                                      className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                  </div>
                                )}
                                
                                {bidderName.toLowerCase() === 'criteo' && (
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Network ID
                                    </label>
                                    <input
                                      type="number"
                                      value={params.networkId || ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        updateBidderParam(contextId, bidderName, 'networkId', value === '' ? '' : parseInt(value));
                                      }}
                                      className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                  </div>
                                )}
                                
                                {bidderName.toLowerCase() === 'sovrn' && (
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Tag ID (Banner)
                                    </label>
                                    <input
                                      type="text"
                                      value={params.tagid || ''}
                                      onChange={(e) => updateBidderParam(contextId, bidderName, 'tagid', e.target.value)}
                                      className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                  </div>
                                )}
                                
                                {bidderName.toLowerCase() === 'smartadserver' && (
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Format ID
                                    </label>
                                    <input
                                      type="text"
                                      value={params.formatId || ''}
                                      onChange={(e) => updateBidderParam(contextId, bidderName, 'formatId', e.target.value)}
                                      className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Video Parameters */}
                            <div className="border border-purple-200 rounded-lg p-3 bg-purple-50">
                              <h6 className="font-medium text-purple-800 mb-3 flex items-center">
                                🎥 Video Parameters
                              </h6>
                              <div className="grid grid-cols-2 gap-3">
                                {bidderName.toLowerCase() === 'teads' && (
                                  <>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Video Placement ID
                                      </label>
                                      <input
                                        type="number"
                                        value={params.video?.placementId || ''}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          const videoParams = { ...(params.video || {}) };
                                          if (value === '') {
                                            delete videoParams.placementId;
                                          } else {
                                            videoParams.placementId = parseInt(value);
                                          }
                                          updateBidderParam(contextId, bidderName, 'video', Object.keys(videoParams).length > 0 ? videoParams : undefined);
                                        }}
                                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Leave empty to use banner ID"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Video Page ID
                                      </label>
                                      <input
                                        type="number"
                                        value={params.video?.pageId || ''}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          const videoParams = { ...(params.video || {}) };
                                          if (value === '') {
                                            delete videoParams.pageId;
                                          } else {
                                            videoParams.pageId = parseInt(value);
                                          }
                                          updateBidderParam(contextId, bidderName, 'video', Object.keys(videoParams).length > 0 ? videoParams : undefined);
                                        }}
                                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Leave empty to use banner ID"
                                      />
                                    </div>
                                  </>
                                )}
                                
                                {bidderName.toLowerCase() === 'triplelift' && (
                                  <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Video Inventory Code
                                    </label>
                                    <input
                                      type="text"
                                      value={params.video?.inventoryCode || ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        const videoParams = { ...(params.video || {}) };
                                        if (value === '') {
                                          delete videoParams.inventoryCode;
                                        } else {
                                          videoParams.inventoryCode = value;
                                        }
                                        updateBidderParam(contextId, bidderName, 'video', Object.keys(videoParams).length > 0 ? videoParams : undefined);
                                      }}
                                      className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      placeholder="Leave empty to use banner code"
                                    />
                                  </div>
                                )}
                                
                                {bidderName.toLowerCase() === 'sovrn' && (
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Video Tag ID
                                    </label>
                                    <input
                                      type="text"
                                      value={params.video?.tagid || ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        const videoParams = { ...(params.video || {}) };
                                        if (value === '') {
                                          delete videoParams.tagid;
                                        } else {
                                          videoParams.tagid = value;
                                        }
                                        updateBidderParam(contextId, bidderName, 'video', Object.keys(videoParams).length > 0 ? videoParams : undefined);
                                      }}
                                      className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      placeholder="Leave empty to use banner Tag ID"
                                    />
                                  </div>
                                )}
                                
                                {bidderName.toLowerCase() === 'smartadserver' && (
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Video Format ID
                                    </label>
                                    <input
                                      type="text"
                                      value={params.video?.formatId || ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        const videoParams = { ...(params.video || {}) };
                                        if (value === '') {
                                          delete videoParams.formatId;
                                        } else {
                                          videoParams.formatId = value;
                                        }
                                        updateBidderParam(contextId, bidderName, 'video', Object.keys(videoParams).length > 0 ? videoParams : undefined);
                                      }}
                                      className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      placeholder="Leave empty to use banner Format ID"
                                    />
                                  </div>
                                )}

                                {bidderName.toLowerCase() === 'adf' && (
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Video Placement ID (mid)
                                    </label>
                                    <input
                                      type="number"
                                      value={params.video?.mid || ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        const videoParams = { ...(params.video || {}) };
                                        if (value === '') {
                                          delete videoParams.mid;
                                        } else {
                                          videoParams.mid = parseInt(value);
                                        }
                                        updateBidderParam(contextId, bidderName, 'video', Object.keys(videoParams).length > 0 ? videoParams : undefined);
                                      }}
                                      className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      placeholder="Leave empty to use banner placement ID"
                                    />
                                  </div>
                                )}

                                {/* Note about Criteo video - no separate video params needed */}
                                {bidderName.toLowerCase() === 'criteo' && (
                                  <div className="col-span-2">
                                    <p className="text-sm text-gray-600 bg-gray-100 p-2 rounded">
                                      ℹ️ Criteo uses the same Network ID for both banner and video ads. Video-specific parameters (api, skip) are handled automatically.
                                    </p>
                                  </div>
                                )}
                              </div>
                              <p className="text-xs text-purple-600 mt-2">
                                💡 Video parameters are optional. Leave empty to use banner parameters for video ads.
                              </p>
                            </div>
                          </div>
                        ) : (
                          // Single media type - show regular parameters
                          <div className="grid grid-cols-2 gap-3">
                            {renderBidderParameterFields(bidderName, params, contextId)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
            }).filter(Boolean)}
          </div>

          {/* Video Settings */}
          {(editedPlacement.mediaType === 'video' || editedPlacement.mediaType === 'both') && (
            <div>
              <h3 className="text-lg font-medium mb-4">Video Settings</h3>
              <div className="border border-gray-200 rounded-lg p-4 space-y-4">
                
                {/* Video Context and Player Size */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Video Context
                    </label>
                    <select
                      value={editedPlacement.videoSettings?.context || 'outstream'}
                      onChange={(e) => setEditedPlacement(prev => ({
                        ...prev,
                        videoSettings: {
                          ...prev.videoSettings,
                          context: e.target.value
                        }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="outstream">Outstream</option>
                      <option value="instream">Instream</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Player Size
                    </label>
                    <select
                      value={JSON.stringify(editedPlacement.videoSettings?.playerSize || [640, 360])}
                      onChange={(e) => {
                        const size = JSON.parse(e.target.value);
                        setEditedPlacement(prev => ({
                          ...prev,
                          videoSettings: {
                            ...prev.videoSettings,
                            playerSize: size
                          }
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="[640, 360]">640×360 (Standard)</option>
                      <option value="[854, 480]">854×480 (Large)</option>
                      <option value="[400, 300]">400×300 (Small)</option>
                      <option value="[300, 250]">300×250 (Medium Rectangle)</option>
                    </select>
                  </div>
                </div>

                {/* Duration Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Min Duration (seconds)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="60"
                      value={editedPlacement.videoSettings?.minduration || 5}
                      onChange={(e) => setEditedPlacement(prev => ({
                        ...prev,
                        videoSettings: {
                          ...prev.videoSettings,
                          minduration: parseInt(e.target.value)
                        }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Duration (seconds)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="120"
                      value={editedPlacement.videoSettings?.maxduration || 30}
                      onChange={(e) => setEditedPlacement(prev => ({
                        ...prev,
                        videoSettings: {
                          ...prev.videoSettings,
                          maxduration: parseInt(e.target.value)
                        }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Skip Settings */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Allow Skip
                    </label>
                    <select
                      value={editedPlacement.videoSettings?.skip || 1}
                      onChange={(e) => setEditedPlacement(prev => ({
                        ...prev,
                        videoSettings: {
                          ...prev.videoSettings,
                          skip: parseInt(e.target.value)
                        }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="1">Yes (Skippable)</option>
                      <option value="0">No (Non-skippable)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Skip After (seconds)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={editedPlacement.videoSettings?.skipafter || 6}
                      onChange={(e) => setEditedPlacement(prev => ({
                        ...prev,
                        videoSettings: {
                          ...prev.videoSettings,
                          skipafter: parseInt(e.target.value)
                        }
                      }))}
                      disabled={editedPlacement.videoSettings?.skip === 0}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        editedPlacement.videoSettings?.skip === 0 ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      API Frameworks
                    </label>
                    <select
                      multiple
                      value={(editedPlacement.videoSettings?.api || [2, 7]).map(String)}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                        setEditedPlacement(prev => ({
                          ...prev,
                          videoSettings: {
                            ...prev.videoSettings,
                            api: selected
                          }
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{ height: '85px' }}
                    >
                      <option value="1">VPAID 1.0</option>
                      <option value="2">VPAID 2.0</option>
                      <option value="7">OMID 1.0</option>
                      <option value="3">MRAID-1</option>
                      <option value="6">MRAID-3</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
                  </div>
                </div>

                {/* API Note for Criteo */}
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> The API and Skip settings are required for Criteo video ads. 
                    VPAID 2.0 and OMID 1.0 are recommended for maximum compatibility.
                  </p>
                </div>

                {/* Renderer Settings */}
                <div>
                  <h4 className="text-md font-medium mb-3">Renderer Settings</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editedPlacement.videoSettings?.renderer?.autoPlay !== false}
                        onChange={(e) => setEditedPlacement(prev => ({
                          ...prev,
                          videoSettings: {
                            ...prev.videoSettings,
                            renderer: {
                              ...prev.videoSettings?.renderer,
                              autoPlay: e.target.checked
                            }
                          }
                        }))}
                        className="mr-2"
                      />
                      Auto Play
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editedPlacement.videoSettings?.renderer?.mute !== false}
                        onChange={(e) => setEditedPlacement(prev => ({
                          ...prev,
                          videoSettings: {
                            ...prev.videoSettings,
                            renderer: {
                              ...prev.videoSettings?.renderer,
                              mute: e.target.checked
                            }
                          }
                        }))}
                        className="mr-2"
                      />
                      Mute by Default
                    </label>
                  </div>
                </div>

                {/* Ad Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Advertisement Text
                  </label>
                  <input
                    type="text"
                    value={editedPlacement.videoSettings?.renderer?.adText || 'Advertisement'}
                    onChange={(e) => setEditedPlacement(prev => ({
                      ...prev,
                      videoSettings: {
                        ...prev.videoSettings,
                        renderer: {
                          ...prev.videoSettings?.renderer,
                          adText: e.target.value
                        }
                      }
                    }))}
                    placeholder="Advertisement"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Text displayed during video ad playback</p>
                </div>

                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Video Configuration:</strong> Using In-Renderer-JS for outstream video rendering. 
                    The renderer supports VAST 2.0/3.0 protocols and automatically handles video playback 
                    within the specified player size.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Settings */}
          <div>
            <h3 className="text-lg font-medium mb-4">Settings</h3>
            
            {/* Basic Settings */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={editedPlacement.settings?.lazyLoad || false}
                  onChange={(e) => setEditedPlacement(prev => ({
                    ...prev,
                    settings: { ...(prev.settings || {}), lazyLoad: e.target.checked }
                  }))}
                  className="mr-2"
                />
                Lazy Load
              </label>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editedPlacement.settings?.refresh || false}
                    onChange={(e) => setEditedPlacement(prev => ({
                      ...prev,
                      settings: { ...(prev.settings || {}), refresh: e.target.checked }
                    }))}
                    className="mr-2"
                  />
                  Refresh
                </label>
                {editedPlacement.settings?.refresh && (
                  <div className="ml-6 mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Refresh Interval (seconds)
                    </label>
                    <input
                      type="number"
                      min="15"
                      max="300"
                      value={editedPlacement.settings?.refreshInterval || 30}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (value >= 15 && value <= 300) {
                          setEditedPlacement(prev => ({
                            ...prev,
                            settings: { 
                              ...(prev.settings || {}), 
                              refreshInterval: value
                            }
                          }));
                        }
                      }}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Minimum: 15s, Maximum: 300s. Ad refreshes only when visible.
                    </p>
                  </div>
                )}
              </div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={editedPlacement.settings?.fluid || false}
                  onChange={(e) => setEditedPlacement(prev => ({
                    ...prev,
                    settings: { ...(prev.settings || {}), fluid: e.target.checked }
                  }))}
                  className="mr-2"
                />
                Fluid
              </label>
            </div>

            {/* Lazy Load Margin Settings */}
            {editedPlacement.settings?.lazyLoad && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-md font-medium mb-3">Lazy Load Margins</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Desktop Margin (px)
                    </label>
                    <input
                      type="text"
                      value={editedPlacement.settings?.lazyLoadMargin?.desktop || '800px'}
                      onChange={(e) => setEditedPlacement(prev => {
                        const settings = prev.settings || {};
                        const lazyLoadMargin = settings.lazyLoadMargin || { desktop: '800px', mobile: '600px' };
                        
                        return {
                          ...prev,
                          settings: {
                            ...settings,
                            lazyLoadMargin: {
                              ...lazyLoadMargin,
                              desktop: e.target.value
                            }
                          }
                        };
                      })}
                      placeholder="e.g., 800px, 400px"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Pixel distance before ad becomes visible (e.g., 800px)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mobile Margin (px)
                    </label>
                    <input
                      type="text"
                      value={editedPlacement.settings?.lazyLoadMargin?.mobile || '600px'}
                      onChange={(e) => setEditedPlacement(prev => {
                        const settings = prev.settings || {};
                        const lazyLoadMargin = settings.lazyLoadMargin || { desktop: '800px', mobile: '600px' };
                        
                        return {
                          ...prev,
                          settings: {
                            ...settings,
                            lazyLoadMargin: {
                              ...lazyLoadMargin,
                              mobile: e.target.value
                            }
                          }
                        };
                      })}
                      placeholder="e.g., 600px, 300px"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Pixel distance before ad becomes visible (e.g., 600px)</p>
                  </div>
                </div>
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Tip:</strong> Lazy load margins control when ads start loading relative to the viewport. 
                    Smaller values (like 200px) load ads closer to when they become visible, 
                    while larger values (like 800px) preload ads earlier for better user experience.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Save size={16} />
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlacementEditModal;